import React, { useEffect, useMemo, useState } from "react";
import { UserRoleFriendlyNames } from "../../../Enums/UserRoleEnum";
import { ViewIcon } from "../../../utils/ViewIcons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faUser, faEye, faLock, faChevronDown, faPencil, faSpinner } from "@fortawesome/free-solid-svg-icons";

const Badge = ({ children, className = "" }) => <span className={`va-badge ${className}`}>{children}</span>;

const AccessLevel = ({ level }) => (
  <span className={`va-access-level ${level === 2 ? "va-access-level-full" : "va-access-level-readonly"}`}>
    <FontAwesomeIcon icon={level === 2 ? faLock : faEye} />
    <span>{level === 2 ? "Pełny dostęp" : "Tylko odczyt"}</span>
  </span>
);

const AccessRow = ({ icon, name, level, onEdit }) => (
  <div className="va-access-row">
    <FontAwesomeIcon icon={icon} className="va-access-person-icon" />
    <span className="va-access-person-name">{name}</span>
    <AccessLevel level={level} />
    {onEdit && <div className="va-edit-access" role="button" tabIndex={0} onClick={onEdit} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEdit(); } }} aria-label={`Edytuj dostęp: ${name}`}><FontAwesomeIcon icon={faPencil} /></div>}
  </div>
);

const ViewsMode = ({ views, search, assignableUsers = [], assignableUsersLoading = false, onLoadAssignableUsers, onSaveAccess }) => {
  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [targetType, setTargetType] = useState("role");
  const [target, setTarget] = useState("");
  const [level, setLevel] = useState(1);
  const [editingUserName, setEditingUserName] = useState("");
  const openEditor = (viewGuid, type = "role", value = "", accessLevel = 1) => {
    setEditing({ viewGuid, existing: value !== "", viewName: views.find(v => v.viewGuid === viewGuid)?.name || "Widok" });
    setTargetType(type);
    setTarget(String(value));
    setLevel(accessLevel);
    setEditingUserName(type === "user" && value !== "" ? (views.find(v => v.viewGuid === viewGuid)?.users || []).find(u => String(u.userId) === String(value))?.preferredName || "Użytkownik" : "");
  };
  const submitEditor = async (remove = false) => {
    if (!editing || target === "" || isSaving) return;
    const data = { viewGuid: editing.viewGuid, userId: targetType === "user" ? Number(target) : null, role: targetType === "role" ? Number(target) : null };
    setIsSaving(true);
    try {
      await onSaveAccess(remove ? data : { ...data, accessLevel: Number(level) }, remove);
      setEditing(null);
    } catch (error) {
      // Keep the popup open so the user can retry.
    } finally {
      setIsSaving(false);
    }
  };
  useEffect(() => {
    if (!editing) return undefined;
    const onEscape = (event) => {
      if (event.key === "Escape" && !isSaving) setEditing(null);
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [editing, isSaving]);

  const [expandedRole, setExpandedRole] = useState(null);

  const toggleRole = (role) => setExpandedRole(current => current === role ? null : role);

  const q = (search || "").trim().toLowerCase();

  const grouped = useMemo(() => {
    const filtered = views.filter((v) => {
      if (!q) return true;
      const rolesText = (v.roles || [])
        .map((r) => UserRoleFriendlyNames[r.role] || String(r.role))
        .join(" ");

      const usersText = (v.users || [])
        .map((u) => `${u.preferredName} ${u.email || ""}`)
        .join(" ");

      const hay = `${v.name} ${v.description} ${v.viewGroup} ${rolesText} ${usersText}`.toLowerCase();
            return hay.includes(q);
          });

    const map = new Map();
    for (const v of filtered) {
      const key = v.viewGroup && v.viewGroup.trim() ? v.viewGroup : "(Brak grupy)";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(v);
    }

    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "pl"))
      .map(([groupName, list]) => ({
        groupName,
        list: list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "pl")),
      }));
  }, [views, q]);

  return (
    <div className="va-content">
      {grouped.map((g) => (
        <div key={g.groupName} className="va-group">
          <div className="va-group-title">{g.groupName}</div>

          <div className="va-cards">
            {g.list.map((v) => (
              <div key={v.viewGuid} className="va-card">
                <div className="va-card-header">
                <div className="va-card-title">
                  <ViewIcon name={v.faIcon} className="va-icon" />
                  <span>{v.name}</span>
                </div>

                  <Badge className={v.isPublic ? "va-badge-public" : "va-badge-restricted"}>
                    {v.isPublic ? "Publiczny" : "Ograniczony"}
                  </Badge>
                </div>
                <div className="va-access-list">
                  {(v.roles || []).map((r) => (
                    <div key={`role-${r.role}`}>
                      <div className="va-role-toggle" role="button" tabIndex={0} aria-expanded={expandedRole === r.role} onClick={() => toggleRole(r.role)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleRole(r.role); } }}>
                        <FontAwesomeIcon icon={faUsers} className="va-access-person-icon" />
                        <FontAwesomeIcon icon={faChevronDown} className={`va-role-chevron ${expandedRole === r.role ? "va-role-chevron-open" : ""}`} />
                        <span className="va-access-person-name">{UserRoleFriendlyNames[r.role] || `Rola ${r.role}`}</span>
                        <AccessLevel level={r.accessLevel} />
                      </div>
                      {![4, 7].includes(r.role) && <div className="va-edit-access va-role-edit" role="button" tabIndex={0} onClick={() => openEditor(v.viewGuid, "role", r.role, r.accessLevel)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openEditor(v.viewGuid, "role", r.role, r.accessLevel); } }} aria-label="Edytuj dostęp roli"><FontAwesomeIcon icon={faPencil} /></div>}
                      {expandedRole === r.role && (
                        <div className="va-role-members">
                          {(r.members || []).length
                            ? r.members.join(", ")
                            : "Brak aktywnych osób w grupie"}
                        </div>
                      )}
                    </div>
                  ))}
                  {(v.users || []).map((u) => (
                    <AccessRow
                      key={`user-${u.userGuid}`}
                      icon={faUser}
                      name={u.preferredName}
                      level={u.accessLevel}
                      onEdit={() => openEditor(v.viewGuid, "user", u.userId, u.accessLevel)}
                    />
                  ))}
                  {!(v.roles || []).length && !(v.users || []).length && (
                    <span className="va-muted va-access-empty">Brak przypisanych uprawnień</span>
                  )}
                </div>
                <div className="va-card-actions">
                  <div className="va-add-access" role="button" tabIndex={0} onClick={() => openEditor(v.viewGuid)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openEditor(v.viewGuid); } }}>+ Dodaj dostęp</div>
                </div>

              </div>
            ))}
          </div>
        </div>
      ))}
      {editing && (
        <div className="va-access-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !isSaving) setEditing(null); }}>
          <div className="va-access-editor" role="dialog" aria-modal="true" aria-label={editing.existing ? "Edytuj dostęp" : "Dodaj dostęp"}>
                    <div className="va-access-editor-header">
                      <div><strong>{editing.existing ? "Edytuj dostęp" : "Dodaj dostęp"}</strong><div className="va-access-editor-subtitle">{editing.viewName}</div></div>
                      <div role="button" tabIndex={0} onClick={() => { if (!isSaving) setEditing(null); }} onKeyDown={(e) => { if (!isSaving && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setEditing(null); } }} aria-label="Zamknij">×</div>
                    </div>
                    <div className={`va-access-editor-fields ${isSaving ? "va-fields-saving" : ""}`}>
                      {editing.existing ? (
                        <div className="va-access-editor-target">
                          <span className="va-access-editor-target-label">{targetType === "role" ? "Rola" : "Osoba"}</span>
                          <span className="va-access-editor-target-value">
                            {targetType === "role"
                              ? (UserRoleFriendlyNames[target] || `Rola ${target}`)
                              : editingUserName}
                          </span>
                        </div>
                      ) : (
                        <>
                          <select disabled={isSaving} value={targetType} onChange={(e) => { setTargetType(e.target.value); setTarget(""); if (e.target.value === "user") onLoadAssignableUsers(); }}>
                            <option value="role">Rola</option>
                            <option value="user">Osoba</option>
                          </select>
                          <select disabled={isSaving || (targetType === "user" && assignableUsersLoading)} value={target} onChange={(e) => setTarget(e.target.value)}>
                            <option value="">{targetType === "user" && assignableUsersLoading ? "Ładowanie użytkowników..." : "Wybierz..."}</option>
                            {targetType === "role"
                              ? Object.entries(UserRoleFriendlyNames).filter(([id]) => ![4, 7].includes(Number(id))).map(([id, name]) => <option key={id} value={id}>{name}</option>)
                              : assignableUsers.map(u => <option key={u.id} value={u.id}>{u.preferredName} ({u.email})</option>)}
                          </select>
                        </>
                      )}
                      <select disabled={isSaving} value={level} onChange={(e) => setLevel(Number(e.target.value))}>
                        <option value={1}>Tylko odczyt</option>
                        <option value={2}>Pełny dostęp</option>
                      </select>
                    </div>
                    <div className="va-access-editor-actions">
                      {editing.existing && <div className={`va-revoke-access ${isSaving ? "va-action-disabled" : ""}`} role="button" tabIndex={isSaving ? -1 : 0} onClick={() => { if (!isSaving && window.confirm("Na pewno odebrać ten dostęp?")) submitEditor(true); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!isSaving && window.confirm("Na pewno odebrać ten dostęp?")) submitEditor(true); } }}>Odbierz dostęp</div>}
                      <div className={`va-save-access ${!target || isSaving ? "va-action-disabled" : ""}`} role="button" tabIndex={target && !isSaving ? 0 : -1} aria-disabled={!target || isSaving} onClick={() => { if (target && !isSaving) submitEditor(false); }} onKeyDown={(e) => { if (target && !isSaving && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); submitEditor(false); } }}>{isSaving ? <><FontAwesomeIcon icon={faSpinner} spin /> Zapisywanie...</> : "Zapisz"}</div>
                    </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewsMode;
